// src/entities/analytics/model/mappers.ts
import type { HeatmapCell } from "./customer.types";
import type { TrendDirection } from "./common.types";
import { orderStatusColor } from "@/shared/lib";

/** Tolerate the documented { success, message?, data } envelope; leave raw domain objects intact. */
export function unwrap<T>(
  res: T | { success?: boolean; message?: string; data: T } | null | undefined,
): T | null {
  if (res == null) return null;
  if (
    typeof res === "object" &&
    res !== null &&
    "success" in (res as Record<string, unknown>) &&
    "data" in (res as Record<string, unknown>)
  ) {
    return (res as { data: T }).data;
  }
  return res as T;
}

const HEATMAP_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
// API dow: 0=Sun..6=Sat. Display row index: Mon=0..Sun=6.
function dowToRow(dow: number): number {
  return dow === 0 ? 6 : dow - 1;
}

export interface HeatmapGrid {
  grid: number[][];
  max: number;
  dayLabels: string[];
}

/** Sparse cells -> dense 7x24 grid (Mon..Sun rows, 0..23 cols), zero-filled. */
export function buildHeatmapGrid(cells: HeatmapCell[]): HeatmapGrid {
  const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  for (const c of cells) {
    if (c.dow < 0 || c.dow > 6 || c.hour < 0 || c.hour > 23) continue;
    grid[dowToRow(c.dow)][c.hour] = c.orderCount;
  }
  let max = 0;
  for (const row of grid) for (const v of row) if (v > max) max = v;
  return { grid, max: Math.max(max, 1), dayLabels: [...HEATMAP_DAYS] };
}

export interface DirectionMeta {
  color: string;
  arrow: string;
}
/**
 * Colour and arrow for a change. The API only ever says "up" or "down", so an
 * unchanged metric arrived as "up" — a green ↑ beside "0%" on every card of a
 * store with no sales (15 Oct). Pass the change and zero reads as neutral.
 */
export function directionMeta(direction: TrendDirection, change?: number): DirectionMeta {
  if (change === 0) return { color: "text-tertiary", arrow: "" };
  return direction === "down"
    ? { color: "text-red-600 dark:text-red-400", arrow: "↓" }
    : { color: "text-emerald-600 dark:text-emerald-400", arrow: "↑" };
}

export function formatSignedPercent(n: number): string {
  return `${n > 0 ? "+" : ""}${n}%`;
}

/** Percentage-point delta (value - prev) for percentage KPIs like refund rate. */
export function formatPp(value: number, prevValue: number): string {
  const pp = Math.round((value - prevValue) * 10) / 10;
  return `${pp > 0 ? "+" : ""}${pp}pp`;
}

/**
 * Chart series colour for an order status.
 *
 * This used to hold its own STATUS_COLORS map, which disagreed with the orders
 * table's on three statuses — so the same state was one colour in a chart and
 * another in a pill. There is now one map, in shared/lib (shared rather than
 * entities/order because FSD forbids one entity importing another). These
 * values are almost all the ones this map defined; the exception is CANCELLED,
 * which stays the table's red rather than this map's #D85A30, because orange
 * reads weaker than red for a negative terminal state.
 */
export function statusColor(status: string): string {
  return orderStatusColor(status).color;
}

const PAYMENT_COLORS: Record<string, string> = {
  COD: "#EF9F27",
  FONEPAY: "#1D9E75",
  STRIPE: "#378ADD",
};
export function paymentColor(method: string): string {
  return PAYMENT_COLORS[method] ?? "#9ca3af";
}
